import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getCandidateFeedback, createFeedback, updateFeedback, deleteFeedback, getCandidates } from "../api";
import { AuthContext } from "../AuthContext";
import PopupMessage from "./PopupMessage";
import { feedbackStatusOptions } from "../services/dropdownOptions";

const FeedbackPage = () => {
  const { candidateId } = useParams(); // URL param is 'candidateId'
  const navigate = useNavigate();
  const location = useLocation();
  const candidateData = location.state?.candidate;
  const { user } = useContext(AuthContext); // Get logged-in user

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedDate', direction: 'desc' });
  const [fullCandidateData, setFullCandidateData] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    comment: "",
    status: "HR Round"
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Edit state
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    comment: "",
    status: ""
  });

  // Fetch feedback and candidate data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🚀 FeedbackPage: Starting fetch data for candidate', candidateId);
        setLoading(true);

        // Fetch feedback
        const feedbackResponse = await getCandidateFeedback(candidateId);
        console.log('📥 FeedbackPage: Feedback response received:', feedbackResponse);

        // Fetch candidates to get full candidate data
        const candidatesResponse = await getCandidates();
        console.log('📥 FeedbackPage: Candidates response received:', candidatesResponse);

        if (feedbackResponse.success && candidatesResponse.success) {
          console.log('✅ FeedbackPage: Success - Data loaded');
          setFeedback(feedbackResponse.data || []);

          // Find the specific candidate
          const candidate = candidatesResponse.data.find(c => c.CandidateID == candidateId);
          if (candidate) {
            // Transform data similar to CandidatePage
            const normalizeDate = (value) => {
              if (!value) return null;
              const date = new Date(value);
              return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
            };

            const transformedCandidate = {
              id: candidate.CandidateID,
              name: candidate.FullName,
              email: candidate.Email,
              phone: candidate.Mobile,
              location: candidate.CurrentLocation,
              role: candidate.Role,
              readyToTravel: candidate.ReadyToTravel === 1 || candidate.ReadyToTravel === '1' || candidate.ReadyToTravel === true ? 1 : 0,
              remoteAvailable: candidate.RemoteAvailable === 1 || candidate.RemoteAvailable === '1' || candidate.RemoteAvailable === true ? 1 : 0,
              experience: candidate.YearsOfExperience != null ? candidate.YearsOfExperience : 'Not specified',
              primarySkills: candidate.PrimarySkills || 'Not specified',
              secondarySkills: candidate.SecondarySkills || 'Not specified',
              techStack: candidate.TechStack || 'Not specified',
              skills: candidate.Skills || [candidate.PrimarySkills, candidate.SecondarySkills, candidate.TechStack]
                .filter(Boolean)
                .join(', '),
              workType: candidate.WorkType || 'Not specified',
              noticePeriod: candidate.NoticePeriod || candidate.noticePeriod || 'Not specified',
              c2c: candidate.CTC,
              expectedCTC: candidate.ExpectedCTC || 'Not specified',
              freelanceRate: candidate.FreelanceRate || 'Not specified',
              referrerCode: candidate.ReferrerCode || 'Not specified',
              source: candidate.Source || 'Not specified',
              status: candidate.Status,
              aadharNo: candidate.AadharNo || 'Not specified',
              pancardNo: candidate.PancardNo || 'Not specified',
              appliedDate: candidate.CreatedAt,
              resumeFile: candidate.OriginalFileName,
              resumeUrl: candidate.StoredPath,
              resumeId: candidate.ResumeID,
              feedbackStatus: candidate.FeedbackStatus || 'No feedback',
              feedbackComment: candidate.FeedbackComment || 'No feedback',
              availableFrom: normalizeDate(candidate.AvailableFrom ?? candidate.availableFrom)
            };
            setFullCandidateData(transformedCandidate);
          }
        } else {
          const errorMsg = feedbackResponse.message || candidatesResponse.message || 'Failed to load data';
          setError(errorMsg);
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error('💥 FeedbackPage: Error fetching data:', err);
        setError('Network error while loading data');
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [candidateId]);

  // Filter feedback
  const filteredFeedback = useMemo(() => {
    let filtered = feedback.filter(item => {
      const matchesStatus = !statusFilter || item.Status === statusFilter;

      let matchesDate = true;
      if (startDate || endDate) {
        const itemDate = item.CreatedDate ? new Date(item.CreatedDate).toISOString().split('T')[0] : '';
        if (startDate && itemDate < startDate) matchesDate = false;
        if (endDate && itemDate > endDate) matchesDate = false;
      }

      return matchesStatus && matchesDate;
    });

    // Sort feedback
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [feedback, statusFilter, startDate, endDate, sortConfig]);

  // Get unique statuses
  const uniqueStatuses = [...new Set(feedback.map(item => item.Status).filter(Boolean))];

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle delete feedback
  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      console.log('🗑️ FeedbackPage: Deleting feedback:', feedbackId);
      const response = await deleteFeedback(feedbackId);
      console.log('📥 FeedbackPage: Delete response:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback deleted successfully');
        setSuccessMessage('Feedback deleted successfully! 🗑️');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }
      } else {
        setError(response.message || 'Failed to delete feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error deleting feedback:', err);
      setError('Network error while deleting feedback');
      setShowErrorPopup(true);
    }
  };

  // Handle edit feedback
  const handleEditFeedback = (item) => {
    setEditingFeedbackId(item.FeedbackID);
    setEditFormData({
      comment: item.Comment,
      status: item.Status
    });
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle edit form submit
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData.comment.trim()) {
      setError('Comment is required');
      setShowErrorPopup(true);
      return;
    }

    if (!editFormData.status) {
      setError('Status is required');
      setShowErrorPopup(true);
      return;
    }

    try {
      console.log('🚀 FeedbackPage: Updating feedback:', editingFeedbackId);
      const response = await updateFeedback(editingFeedbackId, editFormData);
      console.log('📥 FeedbackPage: Update response:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback updated successfully');
        setSuccessMessage('Feedback updated successfully! ✏️');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }

        // Reset edit form
        setEditingFeedbackId(null);
        setEditFormData({ comment: "", status: "" });
      } else {
        setError(response.message || 'Failed to update feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error updating feedback:', err);
      setError('Network error while updating feedback');
      setShowErrorPopup(true);
    }
  };

  // Handle form submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.comment.trim()) {
      setError('Comment is required');
      setShowErrorPopup(true);
      return;
    }

    if (!formData.status) {
      setError('Status is required');
      setShowErrorPopup(true);
      return;
    }

    try {
      setFormSubmitting(true);
      console.log('🚀 FeedbackPage: Submitting new feedback:', formData);

      const response = await createFeedback({
        candidateId: candidateId,
        comment: formData.comment,
        status: formData.status,
        createdBy: user?.EmployeeID || user?.Email || user?.username || 'System'
      });

      console.log('📥 FeedbackPage: Response received:', response);

      if (response.success) {
        console.log('✅ FeedbackPage: Feedback created successfully');
        setSuccessMessage('Feedback created successfully! 🎉');
        setShowSuccessPopup(true);

        // Refresh feedback list
        const updatedResponse = await getCandidateFeedback(candidateId);
        if (updatedResponse.success) {
          setFeedback(updatedResponse.data || []);
        }

        // Reset form
        setFormData({
          comment: "",
          status: "HR Round"
        });
        setShowForm(false);
      } else {
        setError(response.message || 'Failed to create feedback');
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error('💥 FeedbackPage: Error creating feedback:', err);
      setError('Network error while creating feedback');
      setShowErrorPopup(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="scrapper-container">
      <div className="scrapper-header">
      </div>

      {/* Candidate Info Card */}
      {fullCandidateData && (
        <div className="feedback-candidate-card">
          <div className="feedback-candidate-info">
            <h3 className="feedback-candidate-name">{fullCandidateData.name}</h3>
            <div className="feedback-candidate-details">
              <p><strong>Role:</strong> {fullCandidateData.role}</p>
              <p><strong>Experience:</strong> {fullCandidateData.experience} years</p>
              <p><strong>Location:</strong> {fullCandidateData.location}</p>
              <p><strong>Source:</strong> {fullCandidateData.source}</p>
              <p><strong>Email:</strong> {fullCandidateData.email}</p>
              <p><strong>Phone:</strong> {fullCandidateData.phone}</p>
              <p><strong>Aadhar No:</strong> {fullCandidateData.aadharNo}</p>
              <p><strong>Pan Card No:</strong> {fullCandidateData.pancardNo}</p>
              <p><strong>Primary Skills:</strong> {fullCandidateData.primarySkills}</p>
              <p><strong>Secondary Skills:</strong> {fullCandidateData.secondarySkills}</p>
              <p><strong>Tech Stack:</strong> {fullCandidateData.techStack}</p>
              <p><strong>Work Type:</strong> {fullCandidateData.workType}</p>
              <p><strong>Ready to Travel:</strong> {fullCandidateData.readyToTravel ? 'Yes' : 'No'}</p>
              <p><strong>Remote Available:</strong> {fullCandidateData.remoteAvailable ? 'Yes' : 'No'}</p>
              <p><strong>Notice Period:</strong> {fullCandidateData.noticePeriod}</p>
              <p><strong>CTC:</strong> {fullCandidateData.c2c || 'N/A'}</p>
              <p><strong>Expected CTC:</strong> {fullCandidateData.expectedCTC}</p>
              <p><strong>Freelance Rate:</strong> {fullCandidateData.freelanceRate}</p>
              <p><strong>Referrer Code:</strong> {fullCandidateData.referrerCode}</p>
              <p><strong>Applied Date:</strong> {fullCandidateData.appliedDate ? new Date(fullCandidateData.appliedDate).toLocaleDateString() : 'N/A'}</p> 
              <p> <strong>Available From:</strong> {fullCandidateData.availableFrom ? new Date(fullCandidateData.availableFrom).toLocaleDateString() : 'Not specified'}</p>
              <p><strong>Resume:</strong> {fullCandidateData.resumeUrl ? <a href={fullCandidateData.resumeUrl} target="_blank" rel="noopener noreferrer" className="download-link">Download</a> : 'No Resume'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="feedback-action-section">
        {fullCandidateData && (
          <button
            className="scrapper-link-button"
            onClick={() => {
              navigate('/resume', {
                state: {
                  candidateData: fullCandidateData,
                  isEditMode: true
                }
              });
            }}
          >
            ✏️ Edit Candidate Details
          </button>
        )}
        <button
          className="scrapper-link-button"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '✕ Close Form' : '➕ Add New Feedback'}
        </button>
      </div>

      {/* Feedback Form */}
      {showForm && (
        <div className="feedback-form-container">
          <h3 className="feedback-form-title">Create New Feedback</h3>
          <form onSubmit={handleFormSubmit} className="feedback-form">
            <div className="feedback-form-group">
              <label className="feedback-form-label">Comment:</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleFormChange}
                placeholder="Enter your feedback comment..."
                className="feedback-form-textarea"
                rows="5"
                required
              />
            </div>

            <div className="feedback-form-group">
              <label className="feedback-form-label">Status:</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleFormChange}
                className="feedback-form-select"
              >
                {feedbackStatusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="feedback-form-actions">
              <button
                type="submit"
                className="feedback-btn-submit"
                disabled={formSubmitting}
              >
                {formSubmitting ? '⏳ Submitting...' : '✓ Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="scrapper-filters">
        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="scrapper-filter-select"
          >
            <option value="">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="scrapper-filter-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-filter-label">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="scrapper-filter-input"
          />
        </div>
      </div>

      {/* Feedback Table */}
      <div className="scrapper-table-container">
        <table className="scrapper-table">
          <thead>
            <tr>
              <th className="scrapper-th srno-column">SrNo</th>
              <th className="scrapper-th scrapper-th-sortable status-column" onClick={() => handleSort('Status')}>
                Status
                {sortConfig.key === 'Status' ? (
                  <span className="scrapper-sort-indicator">
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                ) : (
                  <span className="scrapper-sort-unsorted">↕</span>
                )}
              </th>
              <th className="scrapper-th comment-column">Comment</th>
              <th className="scrapper-th scrapper-th-sortable createdby-column" onClick={() => handleSort('CreatedBy')}>
                Created By
                {sortConfig.key === 'CreatedBy' ? (
                  <span className="scrapper-sort-indicator">
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                ) : (
                  <span className="scrapper-sort-unsorted">↕</span>
                )}
              </th>
              <th className="scrapper-th scrapper-th-sortable createddate-column" onClick={() => handleSort('CreatedDate')}>
                Created Date
                {sortConfig.key === 'CreatedDate' ? (
                  <span className="scrapper-sort-indicator">
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                ) : (
                  <span className="scrapper-sort-unsorted">↕</span>
                )}
              </th>
              {/* <th className="scrapper-th">Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="scrapper-no-data scrapper-loading-cell">
                  <div className="scrapper-loading-container">
                    <span className="scrapper-spinner-loader"></span>
                    Loading feedback...
                  </div>
                </td>
              </tr>
            ) : filteredFeedback.length === 0 ? (
              <tr>
                <td colSpan={5} className="scrapper-no-data">
                  {feedback.length === 0 ? 'No feedback found. Create one!' : 'No feedback matches the current filters.'}
                </td>
              </tr>
            ) : (
              filteredFeedback.map((item, idx) => (
                <tr key={`${item.FeedbackID}-${idx}`} className="scrapper-tr-hover">
                  <td className="scrapper-td srno-column">
                    {idx + 1}
                  </td>
                  <td className="scrapper-td status-column">
                    <span className={`scrapper-status-badge scrapper-status-feedback`}>
                      {item.Status || 'No status'}
                    </span>
                  </td>
                  <td className="scrapper-td scrapper-cell-wrap comment-column">
                    {item.Comment || 'No comment'}
                  </td>
                  <td className="scrapper-td createdby-column">
                    {item.CreatedBy || 'N/A'}
                  </td>
                  <td className="scrapper-td createddate-column">
                    {item.CreatedDate ? new Date(item.CreatedDate).toLocaleDateString() : 'N/A'}
                  </td>
                  {/* <td className="scrapper-td">
                    <div className="scrapper-action-buttons">
                      <button
                        type="button"
                        className="edit-btn scrapper-action-button"
                        onClick={() => handleEditFeedback(item)}
                        title="Edit feedback"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        type="button"
                        className="delete-btn scrapper-action-button"
                        onClick={() => handleDeleteFeedback(item.FeedbackID)}
                        title="Delete feedback"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td> */}
                </tr>
              ))
            )}
            {/* Edit Feedback Row */}
            {editingFeedbackId && (
              <tr className="scrapper-edit-row">
                <td colSpan={5} className="scrapper-edit-cell">
                  <div className="feedback-edit-form">
                    <h4>Edit Feedback</h4>
                    <form onSubmit={handleEditFormSubmit} className="feedback-form-inline">
                      <div className="feedback-form-row">
                        <div className="feedback-form-group" style={{ flex: 2 }}>
                          <label>Comment:</label>
                          <textarea
                            name="comment"
                            value={editFormData.comment}
                            onChange={handleEditFormChange}
                            className="feedback-form-textarea"
                            rows="3"
                            required
                          />
                        </div>
                        <div className="feedback-form-group" style={{ flex: 1 }}>
                          <label>Status:</label>
                          <select
                            name="status"
                            value={editFormData.status}
                            onChange={handleEditFormChange}
                            className="feedback-form-select"
                            required
                          >
                            <option value="">-- Select Status --</option>
                            {feedbackStatusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="feedback-edit-actions">
                        <button type="submit" className="feedback-btn-submit">✓ Save</button>
                        <button
                          type="button"
                          className="feedback-btn-cancel"
                          onClick={() => {
                            setEditingFeedbackId(null);
                            setEditFormData({ comment: "", status: "" });
                          }}
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Error Popup */}
      <PopupMessage
        show={showErrorPopup}
        message={error}
        type="error"
        onClose={() => setShowErrorPopup(false)}
      />

      {/* Success Popup */}
      <PopupMessage
        show={showSuccessPopup}
        message={successMessage}
        type="success"
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default FeedbackPage;